import React from 'react';
import { getIn, useFormikContext } from 'formik';

import { FORMIK_AU_TFN_FIELD_IDS } from '~/components/Contacts/UpsertContact/Tabs/AuTfn/constants';
import { getFullAuTfnPath } from '~/components/Contacts/UpsertContact/Tabs/AuTfn/utils';
import UploadDocument from '~/components/Contacts/UpsertContact/UploadDocument';
import { DOCUMENT_FIELD_IDS } from '~/components/Contacts/UpsertContact/UploadDocument/constants';
import { useContact } from '~/components/Contacts/UpsertContact/useContact';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Link from '~/components/core/Atomic/Link/Link';
import Caption from '~/components/core/TextComponents/Caption';
import { useCms } from '~/components/hooks/useCms';
import CloseIcon from '~/components/icons/CloseIcon';

interface TfnDeclarationDocumentProps {
  disabled?: boolean;
  showOnly?: boolean;
  documentUploadPercent?: number;
}

const TfnDeclarationDocument: React.FC<TfnDeclarationDocumentProps> = ({
  disabled,
  showOnly,
  documentUploadPercent = 0,
}) => {
  const { originalContact, isCreation } = useContact();
  const { userOrganization } = useCms();
  const { values, setFieldValue } = useFormikContext();

  const hasExistingDocument = Boolean(originalContact?.au_tfn_contact_document_id);
  const hasFileAssigned = Boolean(getIn(values, getFullAuTfnPath(DOCUMENT_FIELD_IDS.FILE)));
  const isMarkedForRemoval = Boolean(getIn(values, getFullAuTfnPath(FORMIK_AU_TFN_FIELD_IDS.REMOVE_TFN_DOCUMENT)));

  const handleRemoveDocument = () => {
    setFieldValue(getFullAuTfnPath(FORMIK_AU_TFN_FIELD_IDS.REMOVE_TFN_DOCUMENT), true);
  };

  // Don't show upload during contact creation (upload requires existing contact ID)
  if (isCreation) {
    return null;
  }

  // Don't show anything if disabled and no existing/assigned document
  if (disabled && !hasExistingDocument && !hasFileAssigned) {
    return null;
  }

  // Show existing document with option to remove
  if (hasExistingDocument && !isMarkedForRemoval && !hasFileAssigned) {
    return (
      <div className="col-span-full">
        <Caption variant={Caption.VARIANTS.LABEL}>TFN Declaration Form</Caption>
        <div className="mt-8 flex items-center gap-8">
          <Link
            className="font-medium text-teal-700 underline"
            href={`/api/v1/organizations/${userOrganization.id}/contacts/${originalContact?.id}/au_tfn`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View Document
          </Link>
          {!disabled && !showOnly && (
            <IconButton size="small" onClick={handleRemoveDocument} aria-label="Remove document">
              <CloseIcon />
            </IconButton>
          )}
        </div>
      </div>
    );
  }

  return (
    <UploadDocument
      disabled={disabled}
      showOnly={showOnly}
      documentFieldKey={FORMIK_AU_TFN_FIELD_IDS.TFN_DOCUMENT_ID}
      documentType="au_tfn_form"
      documentFieldLabel="TFN Declaration Form"
      getFullPath={getFullAuTfnPath}
      documentUploadPercent={documentUploadPercent}
    />
  );
};

export default TfnDeclarationDocument;
